import React, { useState } from 'react';
import { ComparisonResults } from '../utils/parser';

interface ResultsPanelProps {
  results: ComparisonResults;
}

type Tab = 'notFollowingBack' | 'youDontFollowBack' | 'mutuals';

const tabConfig: { key: Tab; label: string; emoji: string; color: string }[] = [
  { key: 'notFollowingBack', label: 'Not Following Back', emoji: '👻', color: 'text-pink-400' },
  { key: 'youDontFollowBack', label: "You Don't Follow", emoji: '🤷', color: 'text-purple-400' },
  { key: 'mutuals', label: 'Mutuals', emoji: '💚', color: 'text-green-400' },
];

function downloadCSV(usernames: string[], filename: string) {
  const csv = 'username,instagram_url\n' + usernames.map((u) => `${u},https://www.instagram.com/${u}/`).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function copyToClipboard(usernames: string[]) {
  navigator.clipboard.writeText(usernames.join('\n')).catch(() => {
    const ta = document.createElement('textarea');
    ta.value = usernames.join('\n');
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  });
}

const ResultsPanel: React.FC<ResultsPanelProps> = ({ results }) => {
  const [activeTab, setActiveTab] = useState<Tab>('notFollowingBack');
  const [copied, setCopied] = useState(false);

  const activeList = results[activeTab];
  const activeConfig = tabConfig.find((t) => t.key === activeTab)!;

  const handleCopy = () => {
    copyToClipboard(activeList);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    downloadCSV(activeList, `purgr-${activeTab}.csv`);
  };

  return (
    <div className="glass-card p-6 md:p-8 animate-slide-up">
      {/* Pill tab bar — primary active indicator */}
      <div className="flex justify-center gap-2 mb-6 flex-wrap">
        {tabConfig.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`tab-btn${activeTab === tab.key ? ' active' : ''}`}
          >
            {tab.emoji} {tab.label}
          </button>
        ))}
      </div>

      {/* Stat cards — informational, still clickable */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        {tabConfig.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="rounded-xl p-4 text-center transition-all duration-200 border border-white/10 bg-white/5 hover:bg-white/8 hover:border-white/20"
          >
            <div className="text-2xl mb-1">{tab.emoji}</div>
            <div className={`text-xl md:text-2xl font-black ${tab.color}`}>
              {results[tab.key].length.toLocaleString()}
            </div>
            <div className="text-white/40 text-xs mt-1 leading-tight">{tab.label}</div>
          </button>
        ))}
      </div>

      {/* Tab actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <span className="text-lg">{activeConfig.emoji}</span>
          <h3 className="text-white font-semibold">{activeConfig.label}</h3>
          <span className={`text-sm font-bold ${activeConfig.color}`}>
            ({activeList.length.toLocaleString()})
          </span>
        </div>

        {activeList.length > 0 && (
          <div className="flex gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white/60 hover:text-white hover:border-white/30 transition-all"
            >
              {copied ? '✅ Copied!' : '📋 Copy all'}
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white/60 hover:text-white hover:border-white/30 transition-all"
            >
              📥 Download CSV
            </button>
          </div>
        )}
      </div>

      {/* User list */}
      {activeList.length === 0 ? (
        <div className="text-center py-12 text-white/30">
          <div className="text-4xl mb-3">🎉</div>
          <p>No accounts in this category!</p>
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto scrollbar-thin space-y-1">
          {activeList.map((username) => (
            <div key={username} className="user-row">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full ig-gradient-bg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {username[0]?.toUpperCase()}
                </div>
                <span className="text-white/85 text-sm font-medium">@{username}</span>
              </div>
              <a
                href={`https://www.instagram.com/${username}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-white/30 hover:text-white/70 transition-colors flex-shrink-0 ml-2"
                onClick={(e) => e.stopPropagation()}
              >
                View ↗
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResultsPanel;
